import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import API_CONFIG from '../../apiConfig';
import ImageUpload from './ImageUpload';
import { examplePresets, ExamplePreset } from './examplePresets';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';

interface LocationState {
  inputImages: string[];
  recreatingFrom: string;
  generatedImages?: string[];
}

const GENERATION_MODE = 'balanced';
/** Credits charged per input image (bulk: count × this rate). */
const GHOST_MANNEQUIN_CREDITS_PER_IMAGE = 0.5;

const GhostMannequin: React.FC = () => {
  const { currentUser, userCredits, refreshUserCredits } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [outputImages, setOutputImages] = useState<string[]>(state?.generatedImages || []);
  const [isExample, setIsExample] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<{ done: number; total: number } | null>(
    null
  );

  const estimatedCreditCost = useMemo(() => {
    if (imageUrls.length === 0) return null;
    return imageUrls.length * GHOST_MANNEQUIN_CREDITS_PER_IMAGE;
  }, [imageUrls.length]);

  const canAffordCredits =
    estimatedCreditCost !== null && userCredits >= estimatedCreditCost;

  const generationPercent =
    generationProgress && generationProgress.total > 0
      ? Math.round((generationProgress.done / generationProgress.total) * 100)
      : 0;

  const generateForSingleImage = async (inputUrl: string): Promise<string[] | null> => {
    const payload = {
      inputImageUrls: [inputUrl],
      userId: currentUser?.uid || 'anonymous',
      mode: GENERATION_MODE,
      numImages: 1,
    };

    const response = await fetch(API_CONFIG.AI_GHOST_MANNEQUIN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
      return data.imageUrls;
    }
    return null;
  };

  useEffect(() => {
    const loadInputImages = async () => {
      if (state?.inputImages && state.inputImages.length > 0) {
        setImageUrls(state.inputImages);
        if (state.generatedImages) {
          setOutputImages(state.generatedImages);
          setIsExample(false);
        }
      }
    };

    loadInputImages();
  }, [state]);

  useEffect(() => {
    if (imageUrls.length > 0) {
      setErrorMessage('');
    }
  }, [imageUrls]);

  const handleDownloadImage = async (imageUrl: string, index: number) => {
    const filename = `${isExample ? 'example' : 'ghost-mannequin'}-${index + 1}.jpg`;
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download image:', error);
      window.open(imageUrl, '_blank');
    }
  };

  const handlePresetSelect = (presetId: number) => {
    const preset = examplePresets.find((p: ExamplePreset) => p.id === presetId);
    if (preset) {
      setOutputImages(preset.results);
      setIsExample(true);
      setImageUrls([preset.preview]);
    }
  };

  const handleGenerate = async () => {
    if (imageUrls.length === 0) return;

    if (!canAffordCredits) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setProcessing(true);
    setIsExample(false);
    setErrorMessage('');
    setOutputImages([]);
    setGenerationProgress({ done: 0, total: imageUrls.length });

    const results: string[] = [];
    let failedCount = 0;

    try {
      for (let i = 0; i < imageUrls.length; i++) {
        const output = await generateForSingleImage(imageUrls[i]);
        if (output && output.length > 0) {
          results.push(...output);
          setOutputImages([...results]);
        } else {
          failedCount += 1;
        }
        setGenerationProgress({ done: i + 1, total: imageUrls.length });
      }

      if (results.length > 0) {
        await refreshUserCredits();
        if (failedCount > 0) {
          setErrorMessage(
            `Generated ${results.length} result(s). ${failedCount} image(s) failed — try again for those inputs.`
          );
        } else {
          setErrorMessage('');
        }
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Failed to generate ghost mannequin:', error);
      setErrorMessage('Failed to generate ghost mannequin. Please try again.');
      if (results.length > 0) {
        setOutputImages(results);
        await refreshUserCredits();
      }
    } finally {
      setProcessing(false);
      setGenerationProgress(null);
    }
  };

  return (
    <div className="w-full relative">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Garment Image</h2>
            <span className="text-sm text-text-description">
              Upload up to 20 garment photos for bulk ghost mannequin generation
            </span>
          </div>
          <div className="p-4 flex flex-col min-h-[400px]">
            <ImageUpload
              imageUrls={imageUrls}
              setImageUrls={setImageUrls}
              isExample={isExample}
              onExampleSelect={handlePresetSelect}
              onUserUpload={() => setIsExample(false)}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Controls</h2>
            <span className="text-sm text-text-description">Configure your ghost mannequin settings</span>
          </div>

          <div className="p-4 space-y-3 flex-1 overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-text-heading">Ghost Mannequin</label>
                <button
                  onClick={() => {
                    setOutputImages([]);
                    setIsExample(false);
                    setErrorMessage('');
                    setImageUrls([]);
                  }}
                  className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded-md transition-colors"
                  title="Clear all settings, images, and results"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="bg-accent-sand rounded-lg p-2">
              <CreditCostPreview
                creditCost={estimatedCreditCost}
                isEstimating={false}
                error={null}
                canAfford={canAffordCredits}
                className="text-xs"
              />
            </div>

            <button
              className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                imageUrls.length > 0
                  ? 'bg-secondary-500 hover:bg-secondary-600 text-white cursor-pointer'
                  : 'bg-gray-300 text-text-description cursor-not-allowed'
              }`}
              onClick={handleGenerate}
              disabled={imageUrls.length === 0 || processing}
            >
              {processing ? (
                <div className="flex items-center justify-center space-x-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>
                    {generationProgress
                      ? `Generating ${generationProgress.done}/${generationProgress.total} (${generationPercent}%)`
                      : 'Generating...'}
                  </span>
                </div>
              ) : (
                <>
                  <i className="fas fa-user-secret mr-2"></i>
                  Generate Ghost Mannequin
                  {imageUrls.length > 1 ? ` (${imageUrls.length})` : ''}
                </>
              )}
            </button>
            {imageUrls.length === 0 && (
              <p className="text-xs text-text-description text-center">Upload an image to enable generation</p>
            )}

            {processing && generationProgress && (
              <div className="w-full">
                <div className="flex justify-between text-xs text-text-description mb-1">
                  <span>Processing images one by one</span>
                  <span>{generationPercent}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-secondary-500 transition-all duration-300 ease-out"
                    style={{ width: `${generationPercent}%` }}
                  />
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-xs text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Results</h2>
            <span className="text-sm text-text-description">
              {isExample ? 'Example results will appear here' : 'Ghost mannequin outputs will appear here'}
            </span>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            {processing && outputImages.length === 0 ? (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center w-full px-6">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
                  <p className="text-sm text-text-description">
                    {generationProgress
                      ? `Processing image ${generationProgress.done} of ${generationProgress.total}`
                      : 'Creating ghost mannequin effect...'}
                  </p>
                  {generationProgress && (
                    <div className="mt-4 max-w-xs mx-auto">
                      <div className="flex justify-between text-xs text-text-description mb-1">
                        <span>Progress</span>
                        <span>{generationPercent}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-secondary-500 transition-all duration-300 ease-out"
                          style={{ width: `${generationPercent}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : outputImages.length > 0 ? (
              <div className="relative flex-1 min-h-[400px]">
                <div
                  className={`${
                    outputImages.length === 1 ? 'grid grid-cols-1' : 'grid grid-cols-1 sm:grid-cols-2'
                  } gap-2 max-h-[480px] overflow-y-auto`}
                >
                  {outputImages.map((imageUrl, index) => (
                    <div
                      key={`${imageUrl}-${index}`}
                      className="relative flex flex-col h-72 bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl shadow border border-accent-taupe overflow-hidden"
                    >
                      <div className="absolute top-2 left-2 z-10 bg-secondary-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow">
                        #{index + 1}
                      </div>
                      <div className="flex-1 flex items-center justify-center bg-accent-sand min-h-0 p-2">
                        <img
                          src={imageUrl}
                          alt={`${isExample ? 'Example' : 'Generated'} ghost mannequin ${index + 1}`}
                          className="max-h-full max-w-full object-contain rounded-lg bg-white"
                        />
                      </div>
                      <div className="flex justify-center items-center gap-2 px-3 py-2 bg-white border-t border-accent-taupe">
                        <button
                          type="button"
                          className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                          onClick={() => handleDownloadImage(imageUrl, index)}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                          onClick={() => {
                            navigate('/canvas-editor', {
                              state: {
                                inputImages: [imageUrl],
                                prompt: '',
                                style: '',
                                variations: 1,
                                recreatingFrom: '',
                                tool: 'upscaler',
                              },
                            });
                          }}
                        >
                          Upscale
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {processing && outputImages.length > 0 && generationProgress && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Still processing… {generationProgress.done}/{generationProgress.total} complete ({generationPercent}%)
                  </div>
                )}

                {isExample && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <i className="fas fa-info-circle text-blue-600 mr-2 mt-0.5"></i>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">Example Results</p>
                        <p className="text-xs text-blue-700 mt-1">
                          These are example ghost mannequin results. Upload your own garment image to generate real
                          outputs.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center text-text-description">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm">Upload a garment image and click Generate to see results</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={estimatedCreditCost || 0}
        availableCredits={userCredits}
        featureName="Ghost Mannequin"
      />
    </div>
  );
};

export default GhostMannequin;
